import { Component, inject, signal } from '@angular/core';
import { RouterLink, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../services/auth.service';

@Component({
    selector: 'app-signup',
    standalone: true,
    imports: [RouterLink, FormsModule],
    templateUrl: './signup.html',
    styleUrl: './signup.css',
})
export class Signup {
    private authService = inject(AuthService);
    private router = inject(Router);

    loading = signal(false);
    error = signal<string | null>(null);

    formData = {
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
    };

    signupWithGoogle() {
        this.authService.loginWithGoogle();
    }

    async onSubmit() {
        // Clear previous errors
        this.error.set(null);

        // Validate all fields are filled
        if (
            !this.formData.name ||
            !this.formData.email ||
            !this.formData.password ||
            !this.formData.confirmPassword
        ) {
            this.error.set('Please fill in all fields');
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.formData.email)) {
            this.error.set('Please enter a valid email address');
            return;
        }

        // Validate password requirements (must match Cognito policy)
        if (this.formData.password.length < 8) {
            this.error.set('Password must be at least 8 characters long');
            return;
        }

        if (!/[a-z]/.test(this.formData.password)) {
            this.error.set('Password must contain at least one lowercase letter');
            return;
        }

        if (!/[A-Z]/.test(this.formData.password)) {
            this.error.set('Password must contain at least one uppercase letter');
            return;
        }

        if (!/[0-9]/.test(this.formData.password)) {
            this.error.set('Password must contain at least one number');
            return;
        }

        if (!/[^a-zA-Z0-9]/.test(this.formData.password)) {
            this.error.set('Password must contain at least one symbol (e.g., !@#$%^&*)');
            return;
        }

        // Validate passwords match
        if (this.formData.password !== this.formData.confirmPassword) {
            this.error.set('Passwords do not match');
            return;
        }

        this.loading.set(true);

        try {
            await this.authService.signUpWithEmail(
                this.formData.email,
                this.formData.password,
                this.formData.name,
            );

            // Redirect to verification page or show success message
            // For now, redirect to login with a message
            this.router.navigate(['/login'], {
                queryParams: { message: 'Please check your email to verify your account' },
            });
        } catch (err: any) {
            this.error.set(err.message || 'Failed to create account');
            this.loading.set(false);
        }
    }

    clearError() {
        this.error.set(null);
    }
}
